/**
 * Rendu du statut d'une cellule de comparaison — chunk 9.AB.1.
 *
 * ## Ce que ce composant décide, et ce qu'il ne décide pas
 *
 * Il ne décide **rien** de métier. `cell_status`, `status_reason` et `evidence`
 * sont calculés par le serveur (`Comparison.qualifyCell`) — décision PO : « aucune
 * logique métier importante ne doit être reconstruite côté frontend ». Ici on ne
 * fait que traduire cinq états en quelque chose qu'un conseiller lit d'un coup
 * d'œil.
 *
 * ## Pourquoi `value` n'affiche aucun badge
 *
 * C'est le cas nominal, et de loin le plus fréquent — 78 des 124 cellules du smoke.
 * Le badger reviendrait à décorer 63 % de la matrice pour ne rien dire, et à noyer
 * les quatre états qui, eux, portent une information. Un signal partout n'est plus
 * un signal : c'est l'erreur qu'on vient de corriger sur `needs_review`.
 *
 * ## `not_offered` est le seul état qui montre sa preuve
 *
 * Affirmer qu'un assureur ne couvre pas une garantie est une affirmation forte : elle
 * peut faire écarter une soumission. Le badge porte donc la mention extraite et sa
 * page, pour que le conseiller puisse aller vérifier au document plutôt que de nous
 * croire.
 */

import type { ComparisonCell } from "@kollectivia/shared";
import { useTranslation } from "react-i18next";

const BASE = "comparison.cell_status";

/** Teintes par état. `value` n'en a pas — il ne rend aucun badge. */
const TEINTES: Record<Exclude<ComparisonCell["cell_status"], "value">, string> = {
  not_offered: "bg-slate-100 text-slate-700 ring-slate-300",
  not_detected: "bg-amber-50 text-amber-800 ring-amber-200",
  // 9.AM.1 — NEUTRE, et c'est délibéré : le document est muet, ce n'est ni une
  // alerte ni un défaut de l'assureur. L'ambre appellerait une action qui
  // n'existe pas — il n'y a rien à re-vérifier dans le document.
  not_provided: "bg-slate-100 text-slate-700 ring-slate-300",
  needs_review: "bg-amber-50 text-amber-800 ring-amber-200",
  extraction_failed: "bg-red-50 text-red-700 ring-red-200",
};

export type CellStatusBadgeProps = {
  cell: ComparisonCell;
};

export function CellStatusBadge({ cell }: CellStatusBadgeProps) {
  const { t } = useTranslation();
  if (cell.cell_status === "value") return null;

  // Charge sans `cell_status`, ou portant un état inconnu : on ne rend RIEN.
  //
  // Le typage dit que le champ existe, la réalité ne le garantit pas — une réponse
  // en cache d'avant ce chunk, un proxy qui réécrit, un serveur non redéployé. Sans
  // cette garde, `TEINTES[undefined]` donne `undefined` et le libellé retombe sur
  // `comparison.cell_status.label.undefined` : une **clé i18n brute affichée dans le
  // DOM**, ce que le checklist d'interface interdit explicitement. Détecté par un
  // test existant de la page, dont la fixture précède le nouveau contrat.
  const teinte = TEINTES[cell.cell_status as keyof typeof TEINTES];
  if (!teinte) return null;
  // Le motif est traduit et sert d'infobulle : l'état dit QUOI, le motif dit
  // POURQUOI. Un conseiller qui survole comprend sans ouvrir de documentation.
  const motif = t(`${BASE}.reason.${cell.status_reason}`, { defaultValue: "" });

  return (
    <span className="flex flex-wrap items-center gap-1">
      <span
        title={motif || undefined}
        className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset ${teinte}`}
      >
        {t(`${BASE}.label.${cell.cell_status}`)}
      </span>

      {cell.cell_status === "not_offered" && cell.evidence && (
        <span
          className="text-[10px] text-slate-500"
          title={t(`${BASE}.evidence_title`, {
            field: cell.evidence.field_key,
            mention: cell.evidence.mention,
          })}
        >
          {cell.evidence.citation_page !== null
            ? t(`${BASE}.evidence_with_page`, { page: cell.evidence.citation_page })
            : t(`${BASE}.evidence_no_page`)}
        </span>
      )}
    </span>
  );
}

/**
 * Niveau d'hébergement, affiché **à côté** de la valeur et jamais à sa place.
 *
 * Décision PO : « Ne transforme pas cet ordre en score global de l'offre dans ce
 * chunk. » On indique donc `1/3`, `2/3`, `3/3` comme un niveau de couverture
 * d'hébergement, sans l'agréger à quoi que ce soit — et la valeur textuelle du
 * document reste affichée telle quelle.
 */
export function HospitalRoomLevelBadge({ cell }: { cell: ComparisonCell }) {
  const { t } = useTranslation();
  // Même prudence : un niveau absent, nul ou hors échelle ne rend rien.
  const niveau = cell.hospital_room_level;
  if (typeof niveau !== "number" || niveau < 1 || niveau > 3) return null;
  return (
    <span
      title={t(`${BASE}.room_level_title`)}
      className="bg-brand-50 text-brand-700 ring-brand-200 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1 ring-inset"
    >
      {t(`${BASE}.room_level`, { level: niveau })}
    </span>
  );
}
